import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Candidate {
  id: string;
  name: string;
  email: string;
  createdAt: string;
}

interface Attempt {
  id: string;
  candidateId: string;
  phase: 'benchmark' | 'dojo';
  scorePct?: number;
  passed?: boolean;
  status: string;
  submittedAt?: string;
}

interface AccessCode {
  code: string;
  candidateId: string;
  phase: 'benchmark' | 'dojo';
  expiresAt: string;
  consumedAt?: string;
}

export function AdminDashboard() {
  const { me, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhase, setInvitePhase] = useState<'benchmark' | 'dojo'>('benchmark');
  const [issued, setIssued] = useState<AccessCode | null>(null);
  type EmailResult =
    | { kind: 'sent'; id: string }
    | { kind: 'skipped'; reason: string }
    | { kind: 'failed'; error: string };
  const [issuedEmail, setIssuedEmail] = useState<EmailResult | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (me.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, loading]);

  const refresh = async () => {
    const [cs, ats, acs] = await Promise.all([
      api.get<Candidate[]>('/api/admin/candidates'),
      api.get<Attempt[]>('/api/admin/attempts'),
      api.get<AccessCode[]>('/api/admin/access-codes')
    ]);
    setCandidates(cs);
    setAttempts(ats);
    setCodes(acs);
  };

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    try {
      const r = await api.post<{ code: AccessCode; email: EmailResult }>(
        '/api/admin/candidates/invite',
        { name: inviteName, email: inviteEmail, phase: invitePhase }
      );
      setIssued(r.code);
      setIssuedEmail(r.email);
      setInviteName('');
      setInviteEmail('');
      await refresh();
    } catch (err) {
      const body = (err as { body?: { error?: string; message?: string } }).body;
      setInviteError(body?.message ?? body?.error ?? 'Failed to invite candidate.');
    }
  };

  if (loading) return <p className="loading">Loading…</p>;
  if (me.role !== 'admin') return null;

  return (
    <div className="page-admin">
      <header className="admin-head">
        <h1>Admin</h1>
        <div>
          <span className="muted">{me.email}</span>{' '}
          <button className="btn btn-link" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <section className="card">
        <h2>Invite a candidate</h2>
        <form className="invite-form" onSubmit={invite}>
          <input
            placeholder="Full name"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            required
          />
          <input
            type="email"
            placeholder="email@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <select value={invitePhase} onChange={(e) => setInvitePhase(e.target.value as 'benchmark' | 'dojo')}>
            <option value="benchmark">Benchmark</option>
            <option value="dojo">Dojo</option>
          </select>
          <button className="btn btn-primary" type="submit">
            Issue code & email
          </button>
        </form>
        {issued && (
          <div className="success" style={{ marginTop: 12 }}>
            <p>
              Issued <code>{issued.code}</code> ({issued.phase}) — expires{' '}
              {new Date(issued.expiresAt).toLocaleString()}.
            </p>
            {issuedEmail && issuedEmail.kind === 'sent' && (
              <p>📧 Email sent (id <code>{issuedEmail.id}</code>).</p>
            )}
            {issuedEmail && issuedEmail.kind === 'skipped' && (
              <p className="muted">
                ✉ Email not sent — {issuedEmail.reason} Share the code <code>{issued.code}</code> with the candidate or open <a href={`/take/${issued.code}`} target="_blank" rel="noreferrer">/take/{issued.code}</a>.
              </p>
            )}
            {issuedEmail && issuedEmail.kind === 'failed' && (
              <p className="error">
                ⚠ Email failed: {issuedEmail.error}. The code is still valid — share it manually.
              </p>
            )}
          </div>
        )}
        {inviteError && <p className="error" style={{ marginTop: 12 }}>{inviteError}</p>}
      </section>

      <section>
        <h2>Candidates ({candidates.length})</h2>
        <table className="attempts-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.email}</td>
                <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                <td>
                  <Link to={`/admin/candidates/${c.id}`}>View</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Recent attempts</h2>
        <table className="attempts-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Phase</th>
              <th>Status</th>
              <th>Score</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {attempts
              .slice()
              .sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''))
              .slice(0, 20)
              .map((a) => {
                const cand = candidates.find((c) => c.id === a.candidateId);
                return (
                  <tr key={a.id}>
                    <td>{cand?.name ?? a.candidateId}</td>
                    <td>{a.phase}</td>
                    <td>{a.status}</td>
                    <td>{a.scorePct ?? '—'}%</td>
                    <td>{a.submittedAt ? new Date(a.submittedAt).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Outstanding access codes</h2>
        <ul className="code-list">
          {codes
            .filter((c) => !c.consumedAt && new Date(c.expiresAt).getTime() > Date.now())
            .map((c) => {
              const cand = candidates.find((x) => x.id === c.candidateId);
              return (
                <li key={c.code}>
                  <code>{c.code}</code> · {c.phase} · {cand?.email ?? c.candidateId} ·
                  expires {new Date(c.expiresAt).toLocaleString()}
                </li>
              );
            })}
        </ul>
      </section>
    </div>
  );
}
